import * as axiosMainInstance from 'axios';
import handleSuccess from './handleSuccess';
import handleErrors from './handleError';
import { forbiddenSession, GetApi, sessionExpire } from '../middleware';

const axios = axiosMainInstance.default.create();
axios.interceptors.response.use(handleSuccess, handleErrors);

/**
 * @typedef BaseApiParams
 * @type {object}
 * @property {any} data - data to be sent through api
 * @property {string} accessToken - access token to be sent
 * @property {string} endPoint -
 */

/**
 * @typedef ApiResponse
 * @type {object}
 * @property {object} responseObject - can be null
 * @property {number} code
 * @property {string} message
 */

/**
 * @typedef MultipartExtraParam
 * @type {object}
 * @property {function} onUploadProgress
 */

/**
 * @typedef MultipartApiParams
 * @type {MultipartExtraParam & BaseApiParams}
 */

// helper to normalize response/error and handle auth-related side effects
const handleAuthFrom = (obj = {}) => {
  const status =
    obj?.status ??
    obj?.response?.status ??
    (obj?.data && typeof obj.data === 'object' && obj.data?.status) ??
    undefined;

  const code =
    obj?.code ?? obj?.data?.code ?? obj?.response?.data?.code ?? undefined;

  const message =
    obj?.message ??
    obj?.data?.message ??
    obj?.response?.data?.message ??
    undefined;

  // 401 => session expired
  if (status === 401 || String(code) === '401') {
    sessionExpire();
    return true;
  }

  // 403 or explicit FORBIDDEN_BY_IP => mark IP blocked
  if (
    (status == 403 || String(code) == '403') &&
    message === 'FORBIDDEN_BY_IP'
  ) {
    forbiddenSession();
    return true;
  }

  return false;
};

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise}
 */
export function post(url, { data, accessToken, params, endPoint = '' }) {
  const { cancel, cancelToken } = genCancelToken();

  const headers = {
    ...createHeader({ accessToken }),
    ...(data instanceof FormData
      ? { 'Content-Type': 'multipart/form-data' }
      : {})
  };

  const request = axios
    .post(endPoint + url, data, {
      params,
      cancelToken,
      headers // ✅ use merged headers here
    })
    .then((res) => {
      // handle cases where server indicates 401/403 via different shapes
      handleAuthFrom(res);
      return res;
    })
    .catch((error) => {
      handleAuthFrom(error);
      if (error?.status !== 401 && error?.response?.status !== 401) {
        // preserve existing behavior for non-auth errors
      }
      return error;
    });

  request.cancel = cancel;
  return request;
}

/**
 * @param {string} url - Url endpoint
 * @param {MultipartApiParams}
 * @param {string} endPoint - Optional base url
 * @return {Promise}
 */
export function multipart(
  url,
  { data, onUploadProgress = () => {}, accessToken, endPoint }
) {
  const { cancel, cancelToken } = genCancelToken();
  const body = createFileFormData(data);
  const request = axios
    .post(endPoint + url, body, {
      cancelToken,
      headers: createHeader({
        accessToken,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }),
      onUploadProgress: (progressEvent) => {
        onUploadProgress(progressEvent);
      }
    })
    .then((res) => {
      // handle cases where server indicates 401/403 via different shapes
      handleAuthFrom(res);
      return res;
    })
    .catch((error) => {
      handleAuthFrom(error);
      if (error?.status !== 401 && error?.response?.status !== 401) {
        // preserve existing behavior for non-auth errors
      }
      return error;
    });
  request.cancel = cancel;
  return request;
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise<ApiResponse>}
 */
export function get(url, { data, accessToken, endPoint = '' }) {
  const { cancel, cancelToken } = genCancelToken();
  const request = axios
    .get(endPoint + url, {
      cancelToken,
      headers: createHeader({ accessToken }),
      params: data
    })
    .then((res) => {
      handleAuthFrom(res);
      return res;
    })
    .catch((error) => {
      handleAuthFrom(error);
      return error;
    });
  request.cancel = cancel;
  return request;
}

export function getBlob(url, { data, accessToken, endPoint = '' }) {
  const { cancelToken } = genCancelToken();
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const response = await GetApi(endPoint + url, {
        responseType: 'blob',
        cancelToken,
        headers: createHeader({ accessToken }),
        params: data
      });
      const reader = new window.FileReader();
      reader.readAsDataURL(response);
      reader.onload = function () {
        resolve(reader.result);
      };
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise<ApiResponse>}
 */
export function deleteCall(url, { data, accessToken, endPoint = '' }) {
  const { cancel, cancelToken } = genCancelToken();
  const request = axios
    .delete(endPoint + url, {
      cancelToken,
      headers: createHeader({ accessToken }),
      data
    })
    .then((res) => {
      handleAuthFrom(res);
      return res;
    })
    .catch((error) => {
      handleAuthFrom(error);
      return error;
    });
  request.cancel = cancel;
  return request;
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise}
 */
export function put(url, { data, accessToken, params, endPoint = '' }) {
  const { cancel, cancelToken } = genCancelToken();
  const request = axios
    .put(endPoint + url, data, {
      params,
      cancelToken,
      headers: createHeader({ accessToken })
    })
    .then((res) => {
      handleAuthFrom(res);
      return res;
    })
    .catch((error) => {
      handleAuthFrom(error);
      return error;
    });
  request.cancel = cancel;
  return request;
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise<ApiResponse>}
 */
export function retryGet(url, { data: apiData, accessToken, endPoint = '' }) {
  const { retryConfig = {}, ...data } = apiData;
  const {
    retries,
    retryDelay,
    delayInitialRequest,
    passCondition,
    failCondition
  } = {
    retries: 1000,
    retryDelay: 0,
    delayInitialRequest: false,
    passCondition: ({ code }) => code === 200,
    failCondition: ({ code }) => code === 404,
    ...retryConfig
  };
  const { cancel, cancelToken } = genCancelToken();
  let retryAttemptsMade = 0;
  let response = {};
  const request = new Promise((resolve, reject) => {
    const timeoutRetry = (delay) => setTimeout(retryFunc, delay);
    async function retryFunc() {
      if (retryAttemptsMade < retries) {
        try {
          // eslint-disable-next-line no-plusplus
          retryAttemptsMade++;
          if (process.env.NODE_ENV === 'development')
            response = await GetApi(endPoint + url, {
              cancelToken,
              headers: createHeader({ accessToken }),
              params: data
            });
          if (passCondition(response)) {
            resolve(response);
          } else if (failCondition(response)) {
            reject(response);
          } else {
            timeoutRetry(retryDelay);
          }
        } catch (e) {
          reject(e);
        }
      } else {
        reject(response);
      }
    }
    timeoutRetry(delayInitialRequest ? retryDelay : 0);
  });
  request.cancel = cancel;
  return request;
}

export function createHeader({ accessToken, headers = {} }) {
  const authToken = localStorage.getItem('auth_token');
  const header = {
    'Content-Type': 'application/json',
    ...headers
  };
  if (accessToken) {
    header.Authorization = `Token ${accessToken}`;
  }
  header['Auth-Token'] = authToken;
  return header;
}

function genCancelToken() {
  let cancel;
  const cancelToken = new axiosMainInstance.default.CancelToken((c) => {
    cancel = c;
  });
  return { cancel, cancelToken };
}

function createFileFormData(data) {
  const body = new FormData();
  Object.keys(data).forEach((key) => {
    body.append(key, data[key]);
  });

  return body;
}
